/**
 * Phase 4 D-04 + D-08 + D-10 — Zod transport schemas for the shared
 * shopping list. Used at three boundaries:
 *
 *   1. HTTP request/response bodies on /api/shopping/* (D-04 + V5 input cap T-04-04)
 *   2. WS broadcast envelopes on the `shopping` topic (D-08 — full row payload
 *      EXCEPT for shopping.removed which carries no item body)
 *   3. 409 Conflict body shape on stale-version writes (D-10 last-writer-wins)
 *
 * Single source of truth — both apps/api routes and apps/web mutations import
 * from here so the wire shape cannot drift across the workspace boundary
 * (MED-8 schema-drift guard, mirrors Phase 1 P05 events.ts pattern).
 *
 * Zod v4 API: `z.iso.datetime({offset:true})`, `z.coerce`, `z.discriminatedUnion`.
 */
import { z } from 'zod';
import { CATEGORY_KEYS } from './categories.nb';

/** UUIDv7 / UUIDv4 36-char canonical form. Loose enough to accept either. */
const UuidSchema = z.string().regex(/^[0-9a-f-]{36}$/i, 'invalid_uuid');
const CategoryEnumSchema = z.enum(CATEGORY_KEYS);

/** Canonical ShoppingItem shape (matches DB row 1:1 — D-02). */
export const ShoppingItemSchema = z.object({
  id: UuidSchema,
  text: z.string().min(1).max(200),                    // T-04-04 input cap
  category: CategoryEnumSchema.nullable(),
  category_overridden: z.union([z.literal(0), z.literal(1)]),
  checked: z.union([z.literal(0), z.literal(1)]),
  added_by: UuidSchema,
  added_at: z.iso.datetime({ offset: true }),
  checked_by: UuidSchema.nullable(),
  checked_at: z.iso.datetime({ offset: true }).nullable(),
  removed_at: z.iso.datetime({ offset: true }).nullable(),
  removed_by: UuidSchema.nullable(),
  sort_order: z.number(),
  version: z.number().int().nonnegative(),
  updated_at: z.iso.datetime({ offset: true }),
  updated_by: UuidSchema,
});
export type ShoppingItem = z.infer<typeof ShoppingItemSchema>;

/** POST /api/shopping/items body (D-04). */
export const AddItemBodySchema = z.object({
  id: UuidSchema.optional(),                            // optional — server fills if absent
  text: z.string().min(1).max(200),
  category: CategoryEnumSchema.nullable().optional(),
  category_overridden: z.boolean().optional(),
  sort_order: z.number().optional(),
});

/** PATCH /api/shopping/items/:id body (D-04 + D-10 — version is REQUIRED). */
export const PatchItemBodySchema = z.object({
  text: z.string().min(1).max(200).optional(),
  category: CategoryEnumSchema.nullable().optional(),
  category_overridden: z.boolean().optional(),
  checked: z.boolean().optional(),
  sort_order: z.number().optional(),
  version: z.number().int().nonnegative(),              // REQUIRED for optimistic concurrency
});

/** DELETE /api/shopping/items/:id?version=N (D-04 + D-10). */
export const DeleteItemQuerySchema = z.object({
  version: z.coerce.number().int().nonnegative(),
});

/** GET /api/shopping/items?since=ISO (optional). */
export const ListItemsQuerySchema = z.object({
  since: z.iso.datetime({ offset: true }).optional(),
});

/** GET /api/shopping/items response. */
export const ListItemsResponseSchema = z.object({
  items: z.array(ShoppingItemSchema),
});

/** WS message envelopes broadcast on the `shopping` topic (D-08 carve-out — full row included). */
export const ShoppingAddedMsg     = z.object({ event: z.literal('added'),     id: UuidSchema, addedBy: UuidSchema,    item: ShoppingItemSchema });
export const ShoppingUpdatedMsg   = z.object({ event: z.literal('updated'),   id: UuidSchema, updatedBy: UuidSchema,  item: ShoppingItemSchema });
export const ShoppingCheckedMsg   = z.object({ event: z.literal('checked'),   id: UuidSchema, checkedBy: UuidSchema,  item: ShoppingItemSchema });
export const ShoppingUncheckedMsg = z.object({ event: z.literal('unchecked'), id: UuidSchema, uncheckedBy: UuidSchema, item: ShoppingItemSchema });
/** D-08: shopping.removed carries NO item body — clients drop by id. */
export const ShoppingRemovedMsg   = z.object({ event: z.literal('removed'),   id: UuidSchema, removedBy: UuidSchema });

export const ShoppingWSMessageSchema = z.discriminatedUnion('event', [
  ShoppingAddedMsg, ShoppingUpdatedMsg, ShoppingCheckedMsg, ShoppingUncheckedMsg, ShoppingRemovedMsg,
]);
export type ShoppingWSMessage = z.infer<typeof ShoppingWSMessageSchema>;

/** 409 Conflict body (D-10). */
export const VersionConflictBody = z.object({
  kind: z.literal('stale_version'),
  server_version: z.number().int().nonnegative(),
  client_version: z.number().int().nonnegative(),
  item: ShoppingItemSchema,
});
